// ---------------------------------------------------------------------------
// TanStack Query hooks for config API
// ---------------------------------------------------------------------------

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type {
  TenantConfigResponse,
  TenantSurveyConfig,
  UpdateTenantConfigRequest,
  CaseConfigResponse,
  CaseSurveySettings,
  UpdateCaseSettingsRequest,
} from "@/types/config";

const TENANT_CONFIG_KEY = "tenant-config";
const CASE_CONFIG_KEY = "case-config";

export function useTenantConfig(tenantId: number) {
  return useQuery({
    queryKey: [TENANT_CONFIG_KEY, tenantId],
    queryFn: () =>
      api
        .post<{ entries: TenantConfigResponse[] }>(`/config/tenant`, {
          entries: [{ orgNodeId: tenantId }],
        })
        .then((r) => r.entries[0]),
  });
}

export function useUpdateTenantConfig(tenantId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateTenantConfigRequest) =>
      api
        .patch<{ entries: TenantSurveyConfig[] }>(`/config/tenant`, {
          entries: [{ orgNodeId: tenantId, ...data }],
        })
        .then((r) => r.entries[0]),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [TENANT_CONFIG_KEY, tenantId],
      });
    },
  });
}

export function useCaseConfig(caseId: number | null) {
  return useQuery({
    queryKey: [CASE_CONFIG_KEY, caseId],
    queryFn: () =>
      api
        .post<{ entries: CaseConfigResponse[] }>(`/config/case`, {
          entries: [{ caseId }],
        })
        .then((r) => r.entries[0]),
    enabled: !!caseId,
  });
}

export function useUpdateCaseSettings(caseId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateCaseSettingsRequest) =>
      api
        .patch<{ entries: CaseSurveySettings[] }>(`/config/case`, {
          entries: [{ caseId, ...data }],
        })
        .then((r) => r.entries[0]),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CASE_CONFIG_KEY, caseId] });
    },
  });
}
